import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Search, MessageCircle, Users, X, Send } from "lucide-react";
import ClientBackground from "../components/ClientBackground";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";

export default function CommunityChat() {
  const navigate = useNavigate();
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("latest");
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);

  const fetchMyQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/children/community/questions/");
      const allQuestions = response.data.results || response.data;
      const myQuestions = allQuestions.filter(q => 
        children.some(child => child.id === q.child)
      );
      setFilteredQuestions(myQuestions);
    } catch (error) {
      console.error("Error fetching my questions:", error);
    } finally {
      setLoading(false);
    }
  }, [children]);

  const handleSearch = useCallback(async () => {
    if (searchQuery.trim() === "") {
      setFilteredQuestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/children/community/questions/search/?q=${searchQuery}`);
      setFilteredQuestions(response.data);
    } catch (error) {
      console.error("Error searching questions:", error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchChildren();
  }, []);

  useEffect(() => {
    if (activeTab === "latest") {
      fetchLatestQuestions();
    } else if (activeTab === "my") {
      fetchMyQuestions();
    }
  }, [activeTab, fetchMyQuestions]);

  useEffect(() => {
    if (activeTab === "search" && searchQuery.trim() !== "") {
      handleSearch();
    } else if (activeTab === "search" && searchQuery.trim() === "") {
      setFilteredQuestions([]);
    }
  }, [searchQuery, activeTab, handleSearch]);

  const fetchChildren = async () => {
    try {
      const response = await api.get("/children/profiles/");
      setChildren(response.data);
      if (response.data.length > 0) {
        setSelectedChild(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching children:", error);
    }
  };

  const fetchLatestQuestions = async () => {
    setLoading(true);
    try {
      const response = await api.get("/children/community/questions/");
      setFilteredQuestions(response.data.results || response.data);
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  };


  const handleCreateQuestion = async () => {
    if (!newQuestion.trim()) return;

    if (!selectedChild) {
      alert("Please create a child profile first before asking questions.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/children/community/questions/", {
        child_id: selectedChild,
        description: newQuestion,
      });
      setNewQuestion("");
      setShowCreateModal(false);
      if (activeTab === "latest") {
        fetchLatestQuestions();
      } else if (activeTab === "my") {
        fetchMyQuestions();
      }
    } catch (error) {
      console.error("Error creating question:", error);
      alert("Failed to create question. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10 flex flex-col min-h-screen pb-24">
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20 shadow-sm">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg font-semibold text-[#1e3a5f]">Community Q&A</h1>
          <div className="w-10" />
        </div>

        <div className="bg-white/20 backdrop-blur-sm px-5 py-4">
          <div className="flex gap-2 mb-4 max-w-2xl mx-auto">
            <button
              onClick={() => setActiveTab("latest")}
              className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all ${
                activeTab === "latest"
                  ? "bg-[#1e3a5f] text-white shadow-md"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              Latest
            </button>
            <button
              onClick={() => setActiveTab("search")}
              className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all ${
                activeTab === "search"
                  ? "bg-[#1e3a5f] text-white shadow-md"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              Search
            </button>
            <button
              onClick={() => setActiveTab("my")}
              className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all ${
                activeTab === "my"
                  ? "bg-[#1e3a5f] text-white shadow-md"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              My Questions
            </button>
          </div>

          {activeTab === "search" && (
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] transition-all shadow-sm"
              />
            </div>
          )}
        </div>

        <div className="flex-1 px-5 py-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-4">
              {filteredQuestions.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">
                    {activeTab === "search" && searchQuery
                      ? "No questions found"
                      : activeTab === "my"
                      ? "You haven't asked any questions yet"
                      : "No questions yet"}
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    {activeTab === "search" && searchQuery
                      ? "Try a different search term"
                      : "Be the first to ask!"}
                  </p>
                </div>
              ) : (
                filteredQuestions.map((question) => (
                  <div
                    key={question.id}
                    onClick={() => navigate(`/community/question/${question.id}`)}
                    className="relative bg-white rounded-3xl p-6 shadow-md hover:shadow-xl transition-all cursor-pointer border border-gray-100 group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#2a4b73] flex items-center justify-center text-white font-semibold text-sm shadow-md">
                            {question.author_name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">
                              {question.author_name || "Anonymous"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(question.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-800 text-base leading-relaxed mb-4 line-clamp-3 group-hover:text-[#1e3a5f] transition-colors">
                      {question.description}
                    </p>

                    <div className="flex items-center gap-6 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-gray-600">
                        <div className="bg-[#e0f2fe] p-2 rounded-full">
                          <MessageCircle className="w-4 h-4 text-[#1e3a5f]" />
                        </div>
                        <span className="text-sm font-medium">
                          {question.response_count || 0} responses
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <div className="bg-[#fef3c7] p-2 rounded-full">
                          <Users className="w-4 h-4 text-[#f59e0b]" />
                        </div>
                        <span className="text-sm font-medium">
                          {question.participant_count || 0} users
                        </span>
                      </div>
                    </div>

                    {question.response_count > 5 && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-gradient-to-r from-[#FDB913] to-[#FFA500] text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          🔥 Trending
                        </span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white px-5 pb-8 pt-4 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] z-20">
        <div className="max-w-md mx-auto">
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full rounded-xl bg-[#1e3a5f] py-4 text-base font-semibold text-white shadow-md transition-transform active:scale-98 flex items-center justify-center gap-2 hover:bg-[#152e4a]"
          >
            <MessageCircle className="w-5 h-5" />
            Ask a Question
          </button>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-lg md:rounded-3xl rounded-t-3xl shadow-2xl transform transition-transform duration-300 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-3xl">
              <h2 className="text-xl font-bold text-[#1e3a5f]">Ask a Question</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              {selectedChild && children.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <p className="text-sm text-gray-600">
                    Posting as:{" "}
                    <span className="font-semibold text-[#1e3a5f]">
                      {children.find(c => c.id === selectedChild)?.first_name}{" "}
                      {children.find(c => c.id === selectedChild)?.last_name}
                    </span>
                  </p>
                </div>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Question
              </label>
              <textarea
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="Describe your question in detail..."
                rows="6"
                className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] resize-none transition-all"
              />
              <p className="text-xs text-gray-500 mt-2">
                Be clear and specific to get the best answers from the community
              </p>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateQuestion}
                disabled={!newQuestion.trim() || submitting}
                className="flex-1 px-6 py-3 bg-[#1e3a5f] text-white rounded-xl font-semibold hover:bg-[#152e4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Post Question
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
